import { c as _c } from "react/compiler-runtime";
import EventIcon from "@mui/icons-material/Event";
import EventRepeatTwoTone from "@mui/icons-material/EventRepeatTwoTone";
import FavoriteBorder from "@mui/icons-material/FavoriteBorder";
import HistoryToggleOff from "@mui/icons-material/HistoryToggleOff";
import SignalCellular0Bar from "@mui/icons-material/SignalCellular0Bar";
import SignalCellular2Bar from "@mui/icons-material/SignalCellular2Bar";
import SignalCellular4Bar from "@mui/icons-material/SignalCellular4Bar";
import { alpha, Box, Button, darken, Divider, Grid, Link as MuiLink, Paper, Skeleton, Stack, type SvgIcon, Typography, useTheme } from "@mui/material";
import type { BoxProps } from "@mui/material/Box";
import React from "react";
import type { Difficulty, QuestionDataQueryResult } from "#/graphql/generated";
import { useI18nContext } from "#/shared/hooks";
import { CircularPercentage } from "#/shared/ui/atoms/CircularPercentage/CircularPercentage";
import { RatingButtons } from "#/shared/ui/atoms/RatingButtons";
import { TopicTag, TopicTagSkeleton } from "#/shared/ui/atoms/TopicTag";
const SummarySkeleton = () => {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      position: "relative"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Stack direction="row" sx={{
      width: "100%",
      justifyContent: "space-between"
    }}><Skeleton width="40%" height="52px" /><Skeleton width="30%" height="32px" /></Stack>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Stack direction="row" spacing={1}><Skeleton width="15%" height="36px" /><Skeleton width="12%" height="36px" /><Skeleton width="16%" height="36px" /><Skeleton width="14%" height="36px" /></Stack>;
    t3 = <Divider />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Stack direction="row" spacing={1}><TopicTagSkeleton /><TopicTagSkeleton width="30%" /><TopicTagSkeleton width="18%" /></Stack>;
    t5 = <Divider />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      display: "flex",
      justifyContent: "center"
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Stack spacing={1} sx={t0}>{t1}{t2}{t3}{t4}{t5}<Box sx={t6}><CircularPercentage size={180}><Stack sx={{
            alignItems: "center",
            pb: 1
          }}><Skeleton width="80px" height="28px" /><Skeleton width="60px" height="32px" /><Skeleton width="100px" height="24px" /></Stack></CircularPercentage></Box></Stack>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
};
const DifficultyIconMap: Record<keyof typeof Difficulty, typeof SvgIcon> = {
  All: SignalCellular0Bar,
  Easy: SignalCellular0Bar,
  Medium: SignalCellular2Bar,
  Hard: SignalCellular4Bar
};
interface QuestionSummaryProps extends Exclude<BoxProps, "position" | "zIndex"> {
  questionDataQuery: QuestionDataQueryResult;
}
export const QuestionSummary: React.FC<QuestionSummaryProps> = t0 => {
  const $ = _c(26);
  const {
    questionDataQuery,
    ...props
  } = t0;
  const theme = useTheme();
  const {
    LL
  } = useI18nContext();
  const question = questionDataQuery.data?.question;
  const difficultyColor = question && theme.palette.question[question.difficulty].main;
  const DifficultyIcon = question && DifficultyIconMap[question.difficulty];
  const shadowColor = darken(theme.palette.primary.dark, 0.5);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      position: "relative",
      zIndex: 10
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const T0 = Paper;
  const t2 = 12;
  const t3 = theme.zIndex.mobileStepper;
  const t4 = question ? "fit-content" : "80%";
  const t5 = 2;
  const t6 = "auto";
  const t7 = 2;
  let t8;
  if ($[1] !== theme.palette.primary.dark) {
    t8 = alpha(darken(theme.palette.primary.dark, 0.5), 0.1);
    $[1] = theme.palette.primary.dark;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  let t9;
  if ($[3] !== theme.palette.primary.dark) {
    t9 = alpha(theme.palette.primary.dark, 0.2);
    $[3] = theme.palette.primary.dark;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== theme.palette.primary.light) {
    t10 = alpha(theme.palette.primary.light, 0.5);
    $[5] = theme.palette.primary.light;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const t11 = `linear-gradient(64deg, ${t8} 0%, ${t9} 35%, ${t10} 100%)`;
  const t12 = "blur( 14px )";
  const t13 = "1px solid rgba( 255, 255, 255, 0.18 )";
  const t14 = `0px 7px 8px -4px ${alpha(shadowColor, 0.2)}, 0px 12px 17px 2px ${alpha(shadowColor, 0.14)}, 0px 5px 22px 4px ${alpha(shadowColor, 0.12)}`;
  let t15;
  if ($[7] !== t11 || $[8] !== t14 || $[9] !== t4 || $[10] !== theme.shape.borderRadius || $[11] !== theme.zIndex.mobileStepper) {
    t15 = {
      zIndex: t3,
      width: t4,
      p: t5,
      mx: t6,
      marginBottom: t7,
      background: t11,
      backdropFilter: t12,
      border: t13,
      boxShadow: t14,
      borderRadius: theme.shape.borderRadius
    };
    $[7] = t11;
    $[8] = t14;
    $[9] = t4;
    $[10] = theme.shape.borderRadius;
    $[11] = theme.zIndex.mobileStepper;
    $[12] = t15;
  } else {
    t15 = $[12];
  }
  let t16;
  if ($[13] !== DifficultyIcon || $[14] !== LL || $[15] !== difficultyColor || $[16] !== question) {
    t16 = question ? <Box sx={{
      display: "flex",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: 2
    }}><Box sx={{
        flexBasis: "512px",
        flexGrow: 1
      }}><Box sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 1
        }}><Typography variant="h4"><span style={{
              fontWeight: "300"
            }}>{question.questionFrontendId}.</span>{" "}<MuiLink href={`https://leetcode.com/problems/${question.titleSlug || ""}`} color="inherit" target="_blank" rel="noreferrer">{question.title}</MuiLink></Typography><Box sx={{
            display: "flex",
            opacity: 0.9
          }}><EventRepeatTwoTone sx={{
              mr: 1
            }} /><Typography variant="subtitle1" sx={{
              lineHeight: 1.1
            }}>{LL.QUESTION_OF_TODAY_LABEL()}</Typography></Box></Box><Box sx={{
          my: 1,
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          opacity: 0.9,
          justifyContent: {
            xs: "space-between",
            sm: "flex-start"
          }
        }}><Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}><Typography variant="subtitle1">{question.categoryTitle}</Typography></Box><div><Box sx={{
              display: "flex",
              height: "100%",
              alignItems: "center",
              color: difficultyColor,
              svg: {
                marginRight: 1
              }
            }}>{DifficultyIcon && <DifficultyIcon />}<Typography>{question.difficulty}</Typography></Box></div><Grid><RatingButtons question={question} /></Grid><Grid><Button startIcon={<FavoriteBorder />} color="inherit">Favorite</Button></Grid></Box><Divider /><Grid container={true} spacing={1} sx={{
          marginTop: 0,
          marginBottom: 1
        }}>{question.topicTags.map(_temp)}</Grid><Divider /></Box><Box sx={{
        display: "flex",
        alignItems: "center",
        position: "relative"
      }}><CircularPercentage value={question.stats.acRate} size={180}><Typography variant="caption">Acceptance</Typography><Typography sx={{
            fontWeight: "bold"
          }}>{question.stats.acRate}%</Typography><Typography variant="caption"><span style={{
              fontWeight: "bold"
            }}>{question.stats.totalAccepted}</span>{" "}/ {question.stats.totalSubmission}</Typography></CircularPercentage></Box></Box> : <SummarySkeleton />;
    $[13] = DifficultyIcon;
    $[14] = LL;
    $[15] = difficultyColor;
    $[16] = question;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] !== T0 || $[19] !== t15 || $[20] !== t16) {
    t17 = <T0 elevation={t2} sx={t15}>{t16}</T0>;
    $[18] = T0;
    $[19] = t15;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== theme.palette.primary.light) {
    t18 = <EventIcon sx={{
      position: "absolute",
      zIndex: -1,
      fontSize: "1300%",
      color: theme.palette.primary.light,
      right: "2%",
      top: 0,
      transform: "translateY(-40%) rotate(20deg)"
    }} />;
    $[22] = theme.palette.primary.light;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== theme.palette.error.dark) {
    t19 = <HistoryToggleOff sx={{
      position: "absolute",
      zIndex: -1,
      fontSize: "190px",
      color: theme.palette.error.dark,
      left: 0,
      bottom: 0,
      transform: "translateY(40%) rotate(0deg)"
    }} />;
    $[24] = theme.palette.error.dark;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  return <Box {...props} sx={[t1, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}>{t17}{t18}{t19}</Box>;
};
function _temp(topic) {
  return <Grid key={topic.slug}><TopicTag topic={topic} /></Grid>;
}